/*!
 * @license
 * Copyright Squiz Australia Pty Ltd. All Rights Reserved.
 */

import { AiType } from '@squiz/dxp-ai-types';
import {
  BUTTON_FORMAT_SOFT,
  BUTTON_THEME_DEFAULT,
  Button,
  DIALOG_SIZE_XL,
  DialogContentProps,
  DialogProps,
  ICON_HISTORY,
  Link,
} from '@squiz/sds';
import { useDialogStore } from 'dxpConsoleCore-mf/State';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { AI_STRINGS } from '../../../Constants/Strings.constants';
import { EDIT_TOOL_DATA_IDS } from '../../EditToolDialog/EditToolDialog.constants';
import { ViewChatBotThreadDialogContent } from '../ViewChatBotThreadDialogContent/ViewChatBotThreadDialogContent';
import { ViewThreadDialogContent } from '../ViewThreadDialogContent/ViewThreadDialogContent';
import { ViewThreadDialogLauncherProps } from './ViewThreadDialogLauncherProps';

/**
 * The button for viewing a content tool thread details.
 * @param {ViewThreadDialogLauncherProps} props Props passed to `<ViewThreadDialogLauncher />`.
 * @returns {JSX.Element} The `<ViewThreadDialogLauncher />` component.
 */
export const ViewThreadDialogLauncher = (props: ViewThreadDialogLauncherProps): JSX.Element | undefined => {
  const { t } = useTranslation();
  const { thread, aiType } = props;
  const labels = useMemo(() => {
    return {
      cancel: t(AI_STRINGS.cancel),
      review: t(AI_STRINGS.review),
      [AiType.chatbot]: {
        inProgressThread: t(AI_STRINGS.inProgressConversation),
        inProgressThreadTitle: t(AI_STRINGS.inProgressConversationTitle),
      },
      [AiType.generative]: {
        inProgressThread: t(AI_STRINGS.inProgressContentTransformation),
        inProgressThreadTitle: t(AI_STRINGS.inProgressContentTransformationTitle),
      },
    };
  }, [t]);

  const { updateActiveDialog } = useDialogStore();

  const dialogProps: DialogProps = {
    classes: 'view-thread-dialog-launcher',
    dialogContent: {
      renderContent: (dialogProps: DialogContentProps): JSX.Element => {
        dialogProps.setButtons([
          <Button
            classes="dialog-cancel-btn"
            dataId={EDIT_TOOL_DATA_IDS.cancelButton}
            format={BUTTON_FORMAT_SOFT}
            key={'cancel-btn'}
            onClick={dialogProps.close}
            text={labels.cancel}
            theme={BUTTON_THEME_DEFAULT}
          />,
        ]);
        return aiType === AiType.chatbot ? (
          <ViewChatBotThreadDialogContent thread={thread} {...dialogProps} />
        ) : (
          <ViewThreadDialogContent thread={thread} {...dialogProps} />
        );
      },
    },
    dialogSize: DIALOG_SIZE_XL,
    heading: labels.review + ' #' + thread.id,
    icon: ICON_HISTORY,
    scrollOnOverflow: false,
    showDividers: false,
    stateHandler: useDialogStore,
  };

  // Handle opening the dialog
  const openDialog = (): void => {
    updateActiveDialog(dialogProps);
  };

  let linkText = thread.title ?? '-';
  let tooltip: string = '';

  if (!thread.title && !thread.isClosed) {
    linkText = labels[aiType].inProgressThread;
    tooltip = labels[aiType].inProgressThreadTitle;
  }

  return (
    <Link
      className="ai_thread_table_name"
      customClick={openDialog}
      hideIcon
      text={linkText}
      title={tooltip}
      type="button"
    />
  );
};
